import React, { useState, useEffect, useContext } from 'react';
import {
  Radio,
  Slider,
  Row,
  Col,
  Typography,
  Space,
  Drawer,
  Divider,
  Collapse,
} from 'antd';
import { assetChanged } from '@src/components/scene/editor/components/lib/asset-changed';
import { HexColorPicker } from 'react-colorful';
import * as THREE from 'three';
import { ThemeContext } from 'pages/_app';
import styles from './material-editor.module.css';

const { Text, Title } = Typography;

const BasePath = 'https://d23hwblhugcea5.cloudfront.net/Global/StudioTextures/';

const TEXTURES = [
  `${BasePath}Walls+/1.jpg`,
  `${BasePath}Walls+/2.jpg`,
  `${BasePath}Walls+/3.jpg`,
  `${BasePath}Walls+/4.jpg`,
  `${BasePath}Psych/1.jpg`,
  `${BasePath}Psych/2.jpg`,
  `${BasePath}Psych/3.jpg`,
  `${BasePath}Psych/4.jpg`,

  `${BasePath}Floors/1.jpg`,
  `${BasePath}Floors/2.jpg`,
  `${BasePath}Floors/3.jpg`,
  `${BasePath}Floors/4.jpg`,

  `${BasePath}Basic/1.jpg`,
  `${BasePath}Basic/2.jpg`,
  `${BasePath}Basic/3.jpg`,
  `${BasePath}Basic/4.jpg`,

  `${BasePath}Wood/TX_Denzel_2_Normal.jpg`,
];

const MATERIAL_TYPE = {
  STANDARD: 'STANDARD',
  BASIC: 'BASIC',
};

interface MaterialEditorDrawerProps {
  selectedModel: any;
  selectedModelProps: any;
  dispatchChangeModels: any;
  setSelectedModelProps: any;
  isOpen: boolean;
  onClose: () => void;
}

export default function MaterialEditorDrawer({
  selectedModel,
  selectedModelProps,
  dispatchChangeModels,
  setSelectedModelProps,
  isOpen,
  onClose,
}: MaterialEditorDrawerProps) {
  const [materialType, setMaterialType] = useState<any>();
  const [baseColor, setBaseColor] = useState<any>('0xffffff');
  const [roughness, setRoughness] = useState<any>(0);
  const [metallic, setMetallic] = useState<any>(0);

  const threeObj = selectedModel?.props?.asset?.ref?.current;
  const { isDark } = useContext(ThemeContext);

  useEffect(() => {
    if (!threeObj) return;

    setMaterialType(MATERIAL_TYPE.STANDARD);
    setBaseColor(`#ffffff`);
  }, [selectedModel]);

  if (!threeObj) return null;

  const getTexture = (url) => {
    return new THREE.TextureLoader().load(url);
  };

  function onChangeMaterialType(event) {
    const val = event.target.value;
    setMaterialType(val);

    threeObj.traverse((child: any) => {
      if (child.isMesh) {
        if (val === MATERIAL_TYPE.STANDARD) {
          child.material = new THREE.MeshStandardMaterial({ color: 0xffffff });
        } else {
          child.material = new THREE.MeshBasicMaterial({ color: 0xffffff });
        }
      }
    });
  }

  function onChangeBaseColor(val) {
    setBaseColor(val);

    threeObj.traverse((child: any) => {
      if (child.isMesh) {
        child.material.color = new THREE.Color(Number(`0x${val.slice(1)}`));
      }
    });
  }

  function onChangeRoughness(val) {
    setRoughness(val);

    threeObj.traverse((child: any) => {
      if (child.isMesh) {
        child.material.roughness = val;
      }
    });
  }

  function onChangeMetallic(val) {
    setMetallic(val);

    threeObj.traverse((child: any) => {
      if (child.isMesh) {
        child.material.metalness = val;
      }
    });
  }

  const changeBaseTexture = ({ texture, object }) => {
    if (texture) {
      const newTexture = getTexture(texture);
      object.traverse((child: any) => {
        if (child.isMesh) {
          let cloneMaterial = child.material.clone();
          cloneMaterial.map = newTexture;
          cloneMaterial.needsUpdate = true;
          child.material = cloneMaterial;
        }
      });
    }
  };

  const changeNormalTexture = ({ texture, object }) => {
    if (texture) {
      const newTexture = getTexture(texture);
      object.traverse((child: any) => {
        if (child.isMesh) {
          let cloneMaterial = child.material.clone();
          cloneMaterial.normalMap = newTexture;
          cloneMaterial.needsUpdate = true;
          child.material = cloneMaterial;
        }
      });
    }
  };

  const changeRoughnessTexture = ({ texture, object }) => {
    if (texture) {
      const newTexture = getTexture(texture);
      object.traverse((child: any) => {
        if (child.isMesh) {
          let cloneMaterial = child.material.clone();
          cloneMaterial.roughnessMap = newTexture;
          cloneMaterial.needsUpdate = true;
          child.material = cloneMaterial;
        }
      });
    }
  };

  const changeMetallicTexture = ({ texture, object }) => {
    if (texture) {
      const newTexture = getTexture(texture);
      object.traverse((child: any) => {
        if (child.isMesh) {
          let cloneMaterial = child.material.clone();
          cloneMaterial.metalnessMap = newTexture;
          cloneMaterial.needsUpdate = true;
          child.material = cloneMaterial;
        }
      });
    }
  };

  const handleTextureClick = (texture, textureType) => {
    const newProps = {
      ...selectedModelProps,
    };

    const prevProps = {
      ...selectedModelProps,
    };

    let textureProperty;
    let changeFunction;

    switch (textureType) {
      case 'base':
        textureProperty = 'textureToChange';
        changeFunction = changeBaseTexture;
        break;
      case 'normal':
        textureProperty = 'textureToChangeNormal';
        changeFunction = changeNormalTexture;
        break;
      case 'roughness':
        textureProperty = 'textureToChangeRoughness';
        changeFunction = changeRoughnessTexture;
        break;
      case 'metallic':
        textureProperty = 'textureToChangeMetalness';
        changeFunction = changeMetallicTexture;
        break;
    }

    newProps[textureProperty] = texture;
    prevProps[textureProperty] = selectedModel.props.asset[textureProperty];

    if (assetChanged(prevProps, newProps, true)) {
      dispatchChangeModels({
        type: 'change',
        newData: newProps,
        prevProps,
        elementId: selectedModel.props.asset.ref.current.uuid,
      });
      selectedModel.props.asset[textureProperty] = texture;

      changeFunction({ texture, object: threeObj });
      setSelectedModelProps(() => newProps);
    }
  };

  const renderTextureGrid = (textureType, title) => (
    <div className={styles.section}>
      <Text strong>{title}</Text>
      <div className={styles.textureGrid}>
        {TEXTURES.map((item, key) => (
          <img
            className={styles.textureItem}
            key={`img-texture-${textureType}-${key}`}
            src={item}
            alt={`${textureType} texture`}
            onClick={() => handleTextureClick(item, textureType)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <Drawer
      title='Material Editor'
      placement='right'
      width={400}
      onClose={onClose}
      open={isOpen}
      destroyOnClose
    >
      <Space direction='vertical' size='large' className={styles.container}>
        <div className={styles.section}>
          <Text strong>Material Type</Text>
          <Radio.Group
            onChange={onChangeMaterialType}
            value={materialType}
            className={styles.radioGroup}
          >
            <Radio value={MATERIAL_TYPE.STANDARD}>Lit</Radio>
            <Radio value={MATERIAL_TYPE.BASIC}>Unlit</Radio>
          </Radio.Group>
        </div>

        <div className={styles.section}>
          <Text strong>Base Color</Text>
          <div className={styles.colorPickerContainer}>
            <HexColorPicker color={baseColor} onChange={onChangeBaseColor} />
          </div>
        </div>

        <div className={styles.section}>
          <Text strong>Roughness</Text>
          <Slider
            value={roughness}
            onChange={onChangeRoughness}
            min={0}
            max={1}
            step={0.01}
            className={styles.sliderContainer}
          />
        </div>

        <div className={styles.section}>
          <Text strong>Metallic</Text>
          <Slider
            value={metallic}
            onChange={onChangeMetallic}
            min={0}
            max={1}
            step={0.01}
            className={styles.sliderContainer}
          />
        </div>

        <Divider />

        <Collapse>
          <Collapse.Panel header='Base Texture' key='1'>
            {renderTextureGrid('base', 'Base Texture')}
          </Collapse.Panel>

          <Collapse.Panel header='Normal Texture' key='2'>
            {renderTextureGrid('normal', 'Normal Texture')}
          </Collapse.Panel>

          <Collapse.Panel header='Roughness Texture' key='3'>
            {renderTextureGrid('roughness', 'Roughness Texture')}
          </Collapse.Panel>

          <Collapse.Panel header='Metallic Texture' key='4'>
            {renderTextureGrid('metallic', 'Metallic Texture')}
          </Collapse.Panel>
        </Collapse>
      </Space>
    </Drawer>
  );
}
